<template>
  <div class="newsletter container">
    <h2>{{ appStore.newsletterTitle ? appStore.newsletterTitle : $t('footer.newsletter.title') }}</h2>
    <p class="body-2">
      {{ appStore.newsletterDescription ? appStore.newsletterDescription : $t('footer.newsletter.content') }}
    </p>
    <form
      class="newsletter-form"
      :class="{ loading: loading }"
      @submit.prevent="submitForm"
    >
      <div class="input-wrapper">
        <GeneralFormInput
          name="email"
          :value="state.email"
          :placeholder="$t('footer.newsletter.placeholder')"
          :errors="v$.email.$errors"
          :disabled="loading"
          @input="onInput"
        />
        <span
          v-if="appStore.subscribeTag !== 'Alumni'"
          class="tag"
        >
          <span
            v-if="appStore.newsletterDisclaimer"
            v-dompurify-html="appStore.newsletterDisclaimer"
          />
          <span v-else>
            {{ $t('footer.newsletter.privacyPolicyLabel') }}
            <Link :blok="blok.privacyPolicy">{{ $t('footer.privacyPolicy.label') }}</Link>
          </span>
        </span>
      </div>

      <GeneralElementsButton
        class="blue"
        :label="$t('footer.newsletter.button.subscribe')"
        :disabled="loading"
        @clicked="submitForm"
      />
    </form>
  </div>

  <GeneralElementsModal
    v-if="appStore.modalVisible && showPopup"
    @close="closeModal"
  >
    <div
      v-if="success"
      class="success"
    >
      <img
        class="icon"
        width="54"
        height="54"
        src="~/assets/icons/send.svg"
        alt="Success icon"
      />

      <span class="title body-3">{{ $t('footer.newsletter.modal.success.title') }}</span>

      <div class="content">
        {{ $t('footer.newsletter.modal.success.content') }}
      </div>

      <GeneralElementsButton
        type="green"
        :label="$t('form.buttons.ok')"
        @clicked="closeModal"
      />
    </div>
    <div
      v-else
      class="error"
    >
      <img
        class="icon"
        width="54"
        height="54"
        src="~/assets/icons/send.svg"
        alt="Error icon"
      />

      <span
        v-if="!alreadySubscribed"
        class="title body-3"
      >
        {{ $t('modal.fail.title') }}
      </span>

      <div class="content">
        <span>{{ error }}</span>
      </div>

      <GeneralElementsButton
        type="green"
        :label="$t('form.buttons.ok')"
        @clicked="closeModal"
      />
    </div>
  </GeneralElementsModal>
</template>

<script setup lang="ts">
  import { useVuelidate } from '@vuelidate/core'
  import { required, email } from '@vuelidate/validators'
  import type ILink from '~/types/Storyblok/Generic/Link'
  import { useAppStore } from '~/stores'
  import IpLocation from '~/services/IpLocation'
  import AirBalticApi from '~/services/AirBalticApi'
  import GTM from '~/utils/GTM'
  import Link from '~/storyblok/Generic/Link.vue'

  defineProps<{
    blok: {
      privacyPolicy: ILink
    }
  }>()

  const { locale, t } = useI18n()
  const appStore = useAppStore()
  const error = ref('')
  const alreadySubscribed = ref(false)
  const success = ref(false)
  const loading = ref(false)
  const showPopup = ref(false)

  const rules = {
    email: {
      required,
      email
    }
  }

  let state = reactive({
    email: ''
  })

  const v$ = useVuelidate(rules, state)

  interface FieldValue {
    name: string
    value: string
  }

  const onInput = (data: FieldValue) => {
    state = Object.assign(state, { [data.name]: data.value })
  }

  const submitForm = async () => {
    const isFormCorrect = await v$.value.$validate()

    if (!isFormCorrect) return

    loading.value = true

    if (!appStore.ipCountryCode) {
      appStore.ipCountryCode = (await new IpLocation().getCountryCode()) ?? 'LV'
    }

    const data = {
      email: state.email,
      subscribeSource: 'careerspage',
      language: locale.value.toUpperCase(),
      country: appStore.ipCountryCode,
      tag: appStore.subscribeTag
    }

    await new AirBalticApi()
      .submitNewsletter(data)
      .then(() => {
        handleSubmitted()
      })
      .catch(({ response }) => {
        success.value = false
        error.value = parseErrors(response.data)
      })

    showModal()

    v$.value.$reset()
    loading.value = false
  }

  const handleSubmitted = () => {
    success.value = true
    state.email = ''

    GTM.newsletterSubmit()
  }

  const parseErrors = (data: any) => {
    if (Object.keys(data.fieldErrors).length) {
      return t(`footer.newsletter.error.general`)
    }

    if (data.code === 'ALREADY_SUBSCRIBED') {
      alreadySubscribed.value = true
      return t(`footer.newsletter.error.subscribed`)
    }

    return ''
  }

  const showModal = () => {
    showPopup.value = true
    appStore.showModal(true)
  }

  const closeModal = () => {
    showPopup.value = false
    appStore.showModal(false)
    alreadySubscribed.value = false
    error.value = ''
  }
</script>

<style lang="scss" scoped>
  @import '~/assets/styles/common.scss';

  .newsletter.container {
    color: $ui-dark-blue;
    padding: rem(40);
    text-align: center;

    h2 {
      font-weight: 500;
      font-size: rem(24);
      line-height: rem(40);
    }

    h2,
    p {
      margin: 0 0 rem(24);
    }

    .newsletter-form {
      margin: auto;
      width: 100%;
      max-width: 416px;
      position: relative;

      &.loading {
        .button {
          background-color: $secondary;
          border-color: $border-grey;
        }
      }

      .input-wrapper {
        margin: 0 0 rem(16);
        text-align: left;

        :deep(a) {
          font-size: rem(14);
        }
      }

      .field-wrapper {
        margin-bottom: rem(8);

        :deep(input) {
          margin: 0 auto;
          width: calc(100% - 17px * 2);

          @include tablet-up {
            border: 1px solid $ui-dark-blue;
            border-radius: 30px;
            height: 16px;
            max-width: unset;
            padding: rem(20);
            width: calc(100% - 40px);
          }
        }
      }

      .button {
        margin: auto;
        max-width: unset;
        width: 100%;

        @include tablet-up {
          background-color: $white;
          background-image: url('~/assets/icons/send.svg');
          background-repeat: no-repeat;
          border-color: transparent;
          border-radius: 24px;
          height: 48px;
          padding: 0;
          position: absolute;
          right: 4px;
          top: 4px;
          width: 48px;
        }

        :deep(.cta) {
          @include tablet-up {
            display: none;
          }
        }
      }
    }
  }

  :deep(.modal) {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    max-width: 288px;

    > div {
      align-items: center;
      display: flex;
      flex-direction: column;
      justify-content: center;
      text-align: center;

      img,
      span,
      .content {
        margin-bottom: rem(24);
      }

      img {
        @include mobile {
          padding-top: 20px;
        }
      }

      .button {
        max-width: 148px;
        width: 100%;
      }
    }
  }
</style>
